import React from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    sourceHasValidMedia,
} from "~components/shared/media";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import { HEADLINE_SIZE } from "~types/lineup";
import {
    type CuratedItem,
    type VariationComponent,
    getMagazineIssueUrl,
    getMagazineIssueTitle,
    getMagazineIssueDate,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentMagazineGrid.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentMagazineGrid.module.css";

export const MEDIA_PRIORITY: MediaFormat[] = [MediaFormat.magazineCoverImage];
const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 4,
    desktopMaxWidth: 353,
    mobileMaxWidth: 715,
    className: styles.magazineCoverImage,
    showCaption: false,
    useImageLink: false,
};
const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.fullWidthContentWell,
];

const PLACEMENT_TO_CLASS = ["start", "inner", "inner", "end"];

type MagazineBlockProps = {
    item: CuratedItem;
    itemIndex: number;
    totalItems: number;
};
const MagazineBlock = ({ item, itemIndex, totalItems }: MagazineBlockProps) => {
    const magazineIssueDate = getMagazineIssueDate(item);
    const magazineIssueTitle = getMagazineIssueTitle(item) || "";
    const magazineIssueUrl = getMagazineIssueUrl(item);

    const mediaConfigWithAltText: MediaConfig = {
        ...MEDIA_CONFIG,
        altText: magazineIssueDate,
    };

    const noBorder = totalItems <= 4;
    const numberOfItemsIsOdd = totalItems % 2 === 1;
    const isBottomRowOfTwo = numberOfItemsIsOdd
        ? itemIndex + 1 === totalItems
        : totalItems - 2 <= itemIndex;

    const itemPosition = itemIndex % 4;
    const fourUpOrphans = totalItems % 4;
    const numberOfItemsInBottomRowOfFour =
        fourUpOrphans === 0 ? 4 : fourUpOrphans;
    const isBottomRowOfFour = Boolean(
        totalItems - numberOfItemsInBottomRowOfFour < itemIndex + 1
    );

    const linkClasses = clsx(
        styles.magazine,
        styles.magazineLink,
        styles[PLACEMENT_TO_CLASS[itemPosition]],
        noBorder && styles.lessThanFour,
        isBottomRowOfTwo && styles.bottomRowOfTwo,
        isBottomRowOfFour && styles.bottomRowOfFour
    );

    return (
        <a
            className={linkClasses}
            href={magazineIssueUrl}
            data-link-index={itemIndex}
            data-component="magazine-link"
        >
            <div data-component="magazine-block">
                <Media
                    source={item}
                    priority={MEDIA_PRIORITY}
                    config={mediaConfigWithAltText}
                />
                <Headline
                    text={magazineIssueTitle}
                    size={HEADLINE_SIZE.EXTRA_SMALL}
                    isPhoenix
                    className={styles.magazineIssueTitle}
                />
                <div
                    className={clsx(
                        styles.subHeadline,
                        typographyStyles.coreShortTextSm
                    )}
                >
                    {magazineIssueDate}
                </div>
            </div>
        </a>
    );
};

export default function LineupContentMagazineGrid(props: VariationComponent) {
    const { items } = props;

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const itemsWithMedia: CuratedItem[] = items?.filter((item) =>
        sourceHasValidMedia(item, MEDIA_PRIORITY)
    );

    if (!itemsWithMedia?.length) return null;

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContentMagazineGrid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={styles.magazineContainer}>
                {itemsWithMedia.map((item, index: number) => (
                    <MagazineBlock
                        item={item}
                        itemIndex={index}
                        totalItems={items.length}
                        key={index}
                    />
                ))}
            </div>
        </VariantBaseWithHeader>
    );
}
